import {
  ClaimsFormData,
  ClaimsFormStatus,
  ClaimsPayer,
  ClaimsSummary,
  ClientSurveyResponsesByYear,
  CountyResult,
  IncentiveSurveyAnswer,
  IncentiveSurveyQuestion,
  Member,
  MEMBER_TYPE,
  Prescription,
  ResultPageIncentiveSurveyData,
  RxSearchResults,
  Survey,
} from 'Containers/ProfilePage/types';
import { newMemberState } from 'Containers/ProfilePage/utils';
import { PAY_PERIOD_VALUES } from 'Types/entities';
import { ClientSurveySchema, JVPEligibilityQuestion } from 'Utils/apiTypes';
import { createTypedMap, TypedMap } from 'Utils/immutable-types';

export interface IProfilePageReducerState {
  prefillHasCompleted: boolean;
  householdIsLoaded: boolean;
  activeMembers: string[];

  // Eligibility section fields
  clientSurvey: Record<string, ClientSurveySchema | null>;
  clientSurveyResponses: ClientSurveyResponsesByYear;
  clientSurveyIsLoaded: boolean;
  clientSurveyIsLoading: boolean;
  clientSurveyError: Error | null;

  jvpEligibilityQuestions: {
    active: JVPEligibilityQuestion[] | null;
    upcoming: JVPEligibilityQuestion[] | null;
  };
  jvpEligibilityQuestionsLoading: boolean;
  jvpEligibilityQuestionsError: Error | null;

  // Location section fields
  zipcode: string; // empty mean unset
  filing_status: string; // empty mean unset
  state_code: string; // empty mean unset
  countyResults: CountyResult[]; // haven't checked this, but probably this type
  countySearchError: Error | null;
  countyIsLoading: boolean;

  // Member section fields

  // todo i think this is 'sso' specific but should probably be consolidated
  //  with jvp behaviors.
  external_id: string; // empty mean unset

  policyholder: Member;
  spouse: Member;
  dependents: Member[];

  searchIsLoading: boolean;
  searchError: Error | null; // TODO is this the right type?

  // Prescription section fields
  rxQuery: string; // empty mean unset
  rxResults: RxSearchResults;
  rxError: Error | null;
  rxIsLoading: boolean;
  rxIsSkipped: boolean;
  prescriptions: Prescription[]; // unsure

  // Capacity to pay, risk assessment, pregnancy fields
  survey: Survey;

  // Optional ben-admin specific field to set payPeriod from Comm-API
  payPeriodsPerYear?: PAY_PERIOD_VALUES;
  hiddenQuestions: { eligibility: string[]; location: string[] };
  incentiveSurveyIsLoading: boolean;
  incentiveSurvey: IncentiveSurveyQuestion[];
  incentiveSurveyAnswers: Record<string, IncentiveSurveyAnswer | null>;
  incentiveSurveyError: Error | null;
  household_id: string | null;
  incentiveSurveyAPICalled: false;
  resultPageIncentiveSurvey: ResultPageIncentiveSurveyData;

  // From AAPI user profile, format is {customerKey}/{publicationSlug}
  // where publication slug is the plan year
  customerProductKey: string | null;

  // Claims Data Fields
  claimsForm: ClaimsFormData;
  claimsFormStatus: ClaimsFormStatus;
  claimsError: Error | null;
  claimsPayersLoading: boolean;
  claimsPayersQuery: string;
  claimsPayers: ClaimsPayer[];
  claimsSummary: ClaimsSummary | null;
  claimsSummaryId: string | null;
  claimsSummaryPolling: boolean;
}

export const initialClaimsFormState: ClaimsFormData = {
  firstName: null,
  lastName: null,
  dateOfBirth: null,
  memberId: null,
  claimsPayer: null,
  isPolicyholder: null,
};

export const initialProfilePageReducerState: IProfilePageReducerState = {
  prefillHasCompleted: false,
  householdIsLoaded: false,
  household_id: null,
  activeMembers: [],
  // Eligibility section fields
  clientSurvey: {},
  clientSurveyResponses: {},
  clientSurveyIsLoaded: false,
  clientSurveyIsLoading: false,
  clientSurveyError: null,

  jvpEligibilityQuestions: {
    active: null,
    upcoming: null,
  },
  jvpEligibilityQuestionsError: null,
  jvpEligibilityQuestionsLoading: false,

  // Location section fields
  zipcode: '',
  filing_status: '',
  state_code: '',
  countyResults: [],
  countySearchError: null,
  countyIsLoading: false,

  // Member section fields

  // todo i think this is 'sso' specific but should probably be consolidated
  //  with jvp behaviors.
  external_id: '',
  policyholder: {
    ...newMemberState(MEMBER_TYPE.POLICYHOLDER),
    utilization: {
      inpatient_days: 0,
      specialist_visits: 0,
      pcp_visits: 0,
    },
    isCovered: true,
  },
  spouse: {
    ...newMemberState(MEMBER_TYPE.SPOUSE_OR_DOMESTIC_PARTNER),
  },
  dependents: [{ ...newMemberState(MEMBER_TYPE.CHILD), uses_tobacco: false }],

  // Prescription section fields
  rxQuery: '',
  rxResults: {
    dropdown: {},
    search: [],
  },
  rxError: null,
  rxIsLoading: false,
  rxIsSkipped: false,
  prescriptions: [],

  searchIsLoading: false,
  searchError: null,

  // Capacity to pay, risk assessment, pregnancy fields
  survey: {
    risk_question_1: '',
    risk_question_2: '',
    plan_child_question: '',
    capacity_to_pay: '',
    enrollment_question: '',
  },
  hiddenQuestions: {
    eligibility: [],
    location: [],
  },
  incentiveSurvey: [],
  incentiveSurveyAnswers: {},
  incentiveSurveyIsLoading: false,
  incentiveSurveyAPICalled: false,
  incentiveSurveyError: null,
  resultPageIncentiveSurvey: { incentiveSurvey: [], incentiveSurveyAnswers: {} },
  customerProductKey: null,

  // Claims fields
  claimsForm: { ...initialClaimsFormState },
  claimsFormStatus: ClaimsFormStatus.Default,
  claimsError: null,
  claimsPayersQuery: '',
  claimsPayersLoading: false,
  claimsPayers: [],
  claimsSummaryId: null,
  claimsSummary: null,
  claimsSummaryPolling: false,
};

export const initialProfileState: TypedMap<IProfilePageReducerState> = createTypedMap(
  initialProfilePageReducerState,
);

export type ProfileState = TypedMap<IProfilePageReducerState>;
